import { type ReactNode, useRef, useState, useEffect } from 'react';
import { cn } from '@/service/utils/cn';
import { Arrow } from '../../Arrows/ArrowDropdown';
import useOnClickOutside from '@/service/hooks/useClickOutside';

type TSelectDropdownProps = {
    options: { value: string; label: string; image?: string; price?: string }[];
    onSelect: (value: string) => void;
    selected?: string;
    isRequired?: boolean;
    placeholder?: string;
    wrapperClassName?: string;
    className?: string;
    groupName?: string;
    elementIndex?: number;
    optionClassName?: string;
    errorClassName?: string;
    arrowSrc?: string;
    errorIcon?: ReactNode;
    errorMessage?: string;
    disabled?: boolean;
    isTouched?: boolean;
};

const DEFAULT_PLACEHOLDER = 'Choose an Option';

export const SelectDropdown = ({
    options,
    onSelect,
    selected,
    placeholder,
    wrapperClassName,
    className,
    optionClassName,
    errorClassName,
    arrowSrc,
    isRequired,
    groupName,
    elementIndex,
    errorIcon,
    errorMessage,
    disabled,
    isTouched,
}: TSelectDropdownProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isClose, setIsClose] = useState(false);
    const [touched, setTouched] = useState(!!isTouched);

    const scrollToTab = (el: HTMLElement) =>
        setTimeout(() => {
            const elementHeight = 265;
            const topPosition = el?.getBoundingClientRect().top;

            if (topPosition && window.innerHeight - topPosition < elementHeight) {
                el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
        }, 100);

    const changeOpen = async (
        e?: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (disabled) {
            setTouched(true);
            setIsClose(false);
            return;
        }
        setIsClose(isOpen);
        if (!touched && isOpen) {
            setTouched(true);
        }
        if (!isOpen) {
            setIsOpen(true);

            if (e) {
                scrollToTab(e.target as HTMLElement);
            }
            setIsClose(false);
        } else {
            setTimeout(() => {
                setIsOpen(false);
                setIsClose(false);
                focusNext();
            }, 0);
        }
    };

    const onSelectOption = async (
        value: string,
        e?: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>
    ) => {
        onSelect(value);
        changeOpen();
        e?.stopPropagation();
    };

    const focusNext = () => {
        if (groupName && typeof elementIndex !== 'undefined') {
            const el = document.querySelector(`#${groupName}-${elementIndex + 1}`);

            if (el) {
                (el as HTMLElement).focus();
            }
        }
    };

    useOnClickOutside(ref, () => isOpen && changeOpen());

    useEffect(() => {
        if (isRequired && isTouched) {
            setTouched(true);
        }
    }, [isTouched, isRequired]);

    const withImages = options.some((option) => !!option.image);

    return (
        <div
            className={cn('relative h-12', wrapperClassName)}
            ref={ref}
            data-test-id="select-dropdown"
        >
            <div
                id={groupName && elementIndex ? `${groupName}-${elementIndex}` : ''}
                tabIndex={0}
                className={cn(
                    'tx-large-medium absolute w-full cursor-pointer rounded-[25px] border border-gray-3 bg-white p-[13px_24px_13px_30px] text-gray-3 dark:border-dark-1 dark:bg-blue-main dark:text-dark-1 md:p-[12px_24px_12px_30px]',
                    isOpen ? 'z-10' : '',
                    isRequired && touched && !isOpen && !selected
                        ? 'border-state-error dark:border-state-error'
                        : '',
                    className || ''
                )}
                onClick={changeOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        changeOpen(e);
                    }
                    if (e.key === 'Escape' && isOpen) {
                        changeOpen(e);
                    }
                }}
            >
                <div className="flex justify-between">
                    <span
                        className={cn(
                            'overflow-hidden text-ellipsis whitespace-nowrap',
                            selected ? 'text-gray-1 dark:text-white' : ''
                        )}
                        data-test-id="selected-value"
                    >
                        {selected || placeholder || DEFAULT_PLACEHOLDER}
                    </span>

                    <Arrow
                        arrowSrc={arrowSrc}
                        toTop={isOpen}
                        className="ml-2 inline-block min-w-[20px] duration-200"
                    />
                </div>
                <div
                    className={cn(
                        'custom-scroll mt-3.5 animate-selectRoll overflow-y-scroll pr-4 text-gray-1 dark:text-white',
                        !isOpen && !isClose ? 'hidden' : 'max-h-[200px]',
                        isClose ? 'max-h-0 animate-selectRollClose' : ''
                    )}
                >
                    {options.map(({ label, value, image, price }) => {
                        return (
                            <div
                                tabIndex={0}
                                key={label}
                                className={
                                    withImages
                                        ? 'grid-rows-[minmax(56px, 1fr)] mb-2 grid min-h-[56px] grid-cols-[80px_1fr] items-center gap-x-4 rounded-2xl bg-ash hover:text-brand dark:bg-dark-3'
                                        : cn(
                                              'cursor-pointer border-t border-gray-3 px-6 py-3 hover:rounded-lg hover:bg-gray-4 dark:border-dark-1 dark:hover:bg-dark-1',
                                              selected && selected === value
                                                  ? 'rounded-lg bg-gray-4 dark:bg-dark-1'
                                                  : '',
                                              optionClassName
                                          )
                                }
                                onClick={(e) => {
                                    onSelectOption(value, e);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onSelectOption(value, e);
                                    }
                                }}
                                data-test-id="select-option"
                            >
                                {withImages &&
                                    (image ? (
                                        <div className="h-full">
                                            <img
                                                src={image}
                                                className="mr-4 h-[60px] min-w-[80px] rounded-2xl bg-gray-3 object-fill"
                                                alt="Option Value Image"
                                            />
                                        </div>
                                    ) : (
                                        <div></div>
                                    ))}
                                <span>
                                    {price && (
                                        <>
                                            <b>{price}</b> -{' '}
                                        </>
                                    )}
                                    {label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            {errorMessage && touched && !isOpen && !selected && (
                <div
                    className={cn(
                        "tx-large-semibold absolute left-0 top-[45px] z-10 flex min-h-[33px] w-full items-center rounded-[15px] border border-gray-4 bg-red-1 px-[15px] pb-[6px] pt-[9px] text-state-error shadow-lightCard before:absolute before:-top-[5px] before:left-[30px] before:h-2 before:w-2 before:-rotate-45 before:border-b-0 before:border-l-0 before:border-r before:border-t before:border-r-gray-4 before:border-t-gray-4 before:bg-red-1 before:content-[''] dark:border-blue-1 dark:bg-red-2 dark:shadow-dark before:dark:border-blue-1 before:dark:bg-red-2 md:top-[47px] lg:top-14 lg:h-10",
                        errorClassName
                    )}
                >
                    <div className="flex w-full items-start gap-x-[6px] md:gap-x-[7px]">
                        {errorIcon}
                        <span>{errorMessage}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
